import React, { useEffect, useState } from 'react';

import type { DrawCaretInstruction } from '@aline/common';
import clsx from 'clsx';
import { equals } from 'ramda';
import { usePrevious } from 'react-use';
import { keyframes } from 'tss-react';
import { makeStyles } from 'tss-react/mui';

import { useFocused, useSelection } from '../hooks';

const CARET_WIDTH = 2;

const caretBlinkAnimation = keyframes`
  0%, 100% { opacity: 1 }
  50% { opacity: 0 }
`;

const useStyles = makeStyles<Pick<DrawCaretInstruction, 'color' | 'label'>>()((
  theme,
  { color, label }
) => {
  // Show labelled cursors with a flat top
  const roundTop = !label;

  return {
    blink: {
      animation: `${caretBlinkAnimation} 1s infinite`,
    },

    caret: {
      backgroundColor: color ?? theme.appColors.primary,
      borderBottomLeftRadius: CARET_WIDTH,
      borderBottomRightRadius: CARET_WIDTH,
      borderTopLeftRadius: roundTop ? CARET_WIDTH : 0,
      borderTopRightRadius: roundTop ? CARET_WIDTH : 0,
      pointerEvents: 'none',
      position: 'absolute',
      transform: `translateX(${-CARET_WIDTH / 2}px)`,
      width: CARET_WIDTH,
      zIndex: theme.appZIndex.caret,
    },

    label: {
      background: color,
      borderRadius: '3px 3px 3px 0px',
      color: 'white',
      fontSize: '12px',
      padding: theme.spacing(0, 0.5),
      pointerEvents: 'none',
      position: 'absolute',
      /**
       * Line the label up with the top-left corner of the caret. We shift it down an additional 1px
       * to prevent a visible gap between the label and the caret in some cases (such as when zooming
       * in) due to floating point error in the CSS engine.
       */
      transform: `translate(${-CARET_WIDTH / 2}px, calc(-100% + 1px))`,
      // Do not wrap labels at the edge of the page
      whiteSpace: 'nowrap',
      zIndex: theme.appZIndex.caret,
    },
  };
});

export function Caret(drawCaretInstruction: DrawCaretInstruction) {
  switch (drawCaretInstruction.caretType) {
    case 'insertion':
      return <InsertionCaret {...drawCaretInstruction} />;

    default:
      return <BaseCaret {...drawCaretInstruction} blink={false} />;
  }
}

function InsertionCaret(drawCaretInstruction: DrawCaretInstruction) {
  const isFocused = useFocused();

  // Check if the selection has changed recently
  const selection = useSelection();
  const prevSelection = usePrevious(selection);
  const [recentSelectionChange, setRecentSelectionChange] = useState(false);
  if (!recentSelectionChange && !equals(selection, prevSelection)) setRecentSelectionChange(true);

  useEffect(() => {
    if (!recentSelectionChange) return;
    const timeout = setTimeout(() => setRecentSelectionChange(false), 500);
    return () => clearTimeout(timeout);
  }, [recentSelectionChange]);

  if (!isFocused) return null;

  return <BaseCaret {...drawCaretInstruction} blink={!recentSelectionChange} />;
}

function BaseCaret({
  blink,
  color,
  height,
  label,
  x,
  y,
}: DrawCaretInstruction & { blink: boolean }) {
  const { classes } = useStyles({ color, label });

  return (
    <>
      <div
        className={clsx(classes.caret, blink && classes.blink)}
        style={{
          height,
          left: x,
          top: y,
        }}
      />

      {label && (
        <div className={classes.label} style={{ left: x, top: y }}>
          {label}
        </div>
      )}
    </>
  );
}
